import React from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Link,
  IconButton,
  Divider,
  Container,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
// Using lucide-react icons as they are natively supported
import {
  Mail,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Shield,
  Headphones,
  Zap,
  Megaphone,
} from "lucide-react";
import Logo from "../../assets/logo.svg";
import StripeLogo from "../../assets/stripe.svg";
import { useSelector } from "react-redux";

// --- Theme Colors (Defined to match user's explicit request) ---
const BG_COLOR = "#F4C542"; // Bright Gold
const HEADING_COLOR = "#000000"; // Black (For Headings, Logo text, and Icons)
const LINK_COLOR = "#ffffff"; // White (For Body Text and Links)
const ACCENT_COLOR = "#FFF2A2"; // Lighter gold for accent/hover

// Custom styled Link component for footer text
const FooterLink = ({ children, ...props }) => (
  <Link
    // Text color is white for contrast against the primary background
    color={LINK_COLOR}
    underline="hover"
    variant="body2"
    sx={{
      cursor: "pointer",
      display: "block",
      transition: "color 0.2s",
      // Hover uses the lighter gold accent color
      "&:hover": { color: ACCENT_COLOR },
    }}
    {...props}
  >
    {children}
  </Link>
);

// --- Data (Unchanged) ---
const navigationLinks = [
  { label: "Home", path: "/" },
  { label: "Market", path: "/market" },
  { label: "Buy Scrap", path: "/buy" },
  { label: "Sell Scrap", path: "/sell" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "Products", path: "/products" },
  { label: "News & Blogs", path: "/blog" },
];

const legalLinks = [
  { label: "About Us", path: "/about-us" },
  { label: "Privacy Policy", path: "/pages/slug/privacy-policy-scraptrade-com-au" },
  { label: "Terms & Conditions", path: "/pages/slug/terms-and-conditions-scraptrade-com-au" },
  { label: "Contact Us", path: "/contact-us" },
  { label: "Sitemap", path: "/sitemap" },
];

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/people/Scrap-Trade/61581102867259/", label: "Facebook" },
  { icon: Twitter, href: "https://x.com/ScrapTradeApp", label: "Twitter" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/scrap-trade/", label: "LinkedIn" },
  { icon: Instagram, href: "https://www.instagram.com/scraptrades/", label: "Instagram" },
];

const Footer = () => {
  const { token, role } = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(token);
  const navigate = useNavigate();
  const theme = useTheme();

  // Define responsive alignment for content (Stack, Box)
  const responsiveAlignment = {
    xs: "flex-start",
    md: "flex-start", // Center up to tablet size
    lg: "flex-start", // Align left on desktop and above
  };

  // Define responsive alignment for text (Grid container)
  const responsiveTextAlign = {
    xs: "left",
    md: "left", // Center up to tablet size
    lg: "left", // Align left on desktop and above
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: BG_COLOR,
        color: LINK_COLOR, // Default body text color
        py: 8,
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          // Use responsive spacing between columns/rows
          spacing={{ xs: 4, sm: 6, md: 8 }}
          justifyContent={{ xs: "space-evenly", md: "space-between" }} // Spread columns out
        >
          {/* Column 1: Logo and Contact Info */}
          <Grid
            item
            xs={12} // Full width on mobile (stacked)
            md={6} // Half width on tablet (2 columns)
            lg={3} // Quarter width on desktop (4 columns)
            sx={{ textAlign: responsiveTextAlign }}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap={1.5}
              // Align content based on screen size
              sx={{ alignItems: responsiveAlignment }}
            >
              {/* Logo */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  cursor: "pointer",
                  mb: 2,
                }}
                onClick={() => navigate("/")}
              >
                {/* <Box
                  sx={{
                    width: 44,
                    height: 44,
                    background: HEADING_COLOR,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                    position: "relative",
                  }}
                > */}
                <Box
                  component="img"
                  src={Logo}
                  alt="ScrapTrade Logo"
                  sx={{ width: "200px", height: "100%" }}
                />
                {/* <Typography
                    sx={{ color: LINK_COLOR, fontWeight: 800, fontSize: "24px" }}
                  >
                    S
                  </Typography> */}
                {/* </Box> */}
                {/* <Typography
                  variant="h5"
                  sx={{
                    color: HEADING_COLOR,
                    fontWeight: 800,
                    fontSize: "28px",
                  }}
                >
                  ScrapTrade
                </Typography> */}
              </Box>

              {/* Contact Emails */}
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                color={HEADING_COLOR}
              >
                Contact Us
              </Typography>
              <Stack
                spacing={1}
                // Align Stack items based on screen size
                sx={{ alignItems: responsiveAlignment }}
              >
                <Typography
                  variant="body2"
                  color={LINK_COLOR}
                  display="flex"
                  alignItems="center"
                  component="a"
                  href="mailto:admin@scraptrade.com.au"
                  sx={{ textDecoration: "none", color: LINK_COLOR, "&:hover": { color: ACCENT_COLOR, textDecoration: "underline" } }}
                >
                  <Shield
                    size={18}
                    style={{ marginRight: 8, color: HEADING_COLOR }}
                  />{" "}
                  admin@scraptrade.com.au
                </Typography>
                <Typography
                  variant="body2"
                  color={LINK_COLOR}
                  display="flex"
                  alignItems="center"
                  component="a"
                  href="mailto:info@scraptrade.com.au"
                  sx={{ textDecoration: "none", color: LINK_COLOR, "&:hover": { color: ACCENT_COLOR, textDecoration: "underline" } }}
                >
                  <Mail
                    size={18}
                    style={{ marginRight: 8, color: HEADING_COLOR }}
                  />{" "}
                  info@scraptrade.com.au
                </Typography>
                <Typography
                  variant="body2"
                  color={LINK_COLOR}
                  display="flex"
                  alignItems="center"
                  component="a"
                  href="mailto:cs@scraptrade.com.au"
                  sx={{ textDecoration: "none", color: LINK_COLOR, "&:hover": { color: ACCENT_COLOR, textDecoration: "underline" } }}
                >
                  <Headphones
                    size={18}
                    style={{ marginRight: 8, color: HEADING_COLOR }}
                  />{" "}
                  cs@scraptrade.com.au
                </Typography>
                <Typography
                  variant="body2"
                  color={LINK_COLOR}
                  display="flex"
                  alignItems="center"
                  component="a"
                  href="mailto:techsupport@scraptrade.com.au"
                  sx={{ textDecoration: "none", color: LINK_COLOR, "&:hover": { color: ACCENT_COLOR, textDecoration: "underline" } }}
                >
                  <Zap
                    size={18}
                    style={{ marginRight: 8, color: HEADING_COLOR }}
                  />{" "}
                  techsupport@scraptrade.com.au
                </Typography>
                <Typography
                  variant="body2"
                  color={LINK_COLOR}
                  display="flex"
                  alignItems="center"
                  component="a"
                  href="mailto:marketing@scraptrade.com.au"
                  sx={{ textDecoration: "none", color: LINK_COLOR, "&:hover": { color: ACCENT_COLOR, textDecoration: "underline" } }}
                >
                  <Megaphone
                    size={18}
                    style={{ marginRight: 8, color: HEADING_COLOR }}
                  />{" "}
                  marketing@scraptrade.com.au
                </Typography>
              </Stack>
            </Box>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid
            item
            xs={12}
            md={6}
            lg={3}
            sx={{ textAlign: responsiveTextAlign }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              color={HEADING_COLOR}
            >
              Quick Links
            </Typography>
            <Stack spacing={1} sx={{ alignItems: responsiveAlignment }}>
              {navigationLinks.map((link, idx) => (
                <React.Fragment key={link.label}>
                  <FooterLink onClick={() => navigate(link.path || "#")}>
                    {link.label}
                  </FooterLink>
                  {/* Insert Login link right after Products */}
                  {link.label === "Products" && !isLoggedIn && (
                    <FooterLink onClick={() => navigate("/login")}>
                      Login
                    </FooterLink>
                  )}
                </React.Fragment>
              ))}
            </Stack>
          </Grid>

          {/* Column 3: Company & Legal Links */}
          <Grid
            item
            xs={12}
            md={6}
            lg={3}
            sx={{ textAlign: responsiveTextAlign }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              color={HEADING_COLOR}
            >
              Company & Legal
            </Typography>
            <Stack spacing={1} sx={{ alignItems: responsiveAlignment }}>
              {legalLinks.map((link) => {
                const isDynamicPage = link.path.startsWith("/pages/slug/");
                if (isDynamicPage) {
                  return (
                    <a
                      key={link.label}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: LINK_COLOR,
                        display: "block",
                        fontSize: "0.875rem",
                        textDecoration: "none",
                        lineHeight: 1.6,
                        transition: "color 0.2s",
                      }}
                      onMouseOver={(e) => (e.target.style.color = ACCENT_COLOR)}
                      onMouseOut={(e) => (e.target.style.color = LINK_COLOR)}
                    >
                      {link.label}
                    </a>
                  );
                }
                return (
                  <FooterLink
                    key={link.label}
                    onClick={() => navigate(link.path)}
                  >
                    {link.label}
                  </FooterLink>
                );
              })}
            </Stack>
          </Grid>

          {/* Column 4: Social Links */}
          <Grid
            item
            xs={12}
            md={6}
            lg={3}
            sx={{ textAlign: responsiveTextAlign }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              color={HEADING_COLOR}
            >
              Connect With Us
            </Typography>
            <Stack spacing={1} sx={{ alignItems: responsiveAlignment }}>
              {socialLinks.map((social) => (
                // Use Box to wrap icon and link for proper alignment
                <Box
                  key={social.label}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <IconButton
                    aria-label={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: HEADING_COLOR,
                      "&:hover": { color: ACCENT_COLOR },
                    }}
                  >
                    <social.icon size={20} />
                  </IconButton>
                  {/* Label next to icon, uses white text */}
                  <FooterLink
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.label}
                  </FooterLink>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {/* Divider - Subtle white line for the dark background */}
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.3)", my: 4 }} />

        {/* Bottom Text - Centered with Logo */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.9rem",
              color: LINK_COLOR,
              textAlign: "center",
            }}
          >
            © {new Date().getFullYear()} ScrapTrade. Secure Checkout
          </Typography>
          <Box
            component="img"
            src={StripeLogo}
            alt="Powered by Stripe"
            sx={{ height: 26, width: "auto" }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
